import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import AutocompleteSelect from '../../../common/components/AutocompleteSelect'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'

import Papa from 'papaparse'

import { apiClient } from '~/api'

import { WALMART_SHOP_ID } from './constants'

// Types
export type Shop = { id: number; shop_name: string }

interface Props {
    shops?: Shop[]
    settings?: Record<string, unknown>
    locations?: unknown[]
    env?: 'sandbox' | 'production'
}

interface Row {
    shop_id: number | ''
    location: string
    location_id: string
    _key: string // stable identity key
    _isNew?: boolean // newly added/edited
    _isDeleted?: boolean // marked for deletion
    _errors?: { shop_id?: boolean; location?: boolean; location_id?: boolean }
}

// Raw API row without UI fields
type APIRow = {
    shop_id: number
    location: string
    location_id: string
}

export interface ShopifyLocationsHandle {
    getDirtyRows: () => [number, string, string | null][]
    resetDirty: () => void
    validate: () => boolean
}

const ShopifyLocations = forwardRef<ShopifyLocationsHandle, Props>(({ shops = [], settings = {}, locations = [], env = 'production' }, ref) => {
    /* ------------------------------ Remote fetch state ------------------------------ */
    const [search, setSearch] = useState('')
    const [filterShopId, setFilterShopId] = useState<number | ''>('')

    const queryClient = useQueryClient()

    const fetchLimit = 25 // Backend uses default PageNumberPagination which ignores limit parameter and always returns 25

    /* ------------------------------- Bulk upload state ------------------------------ */
    const [bulkOpen, setBulkOpen] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadSuccess, setUploadSuccess] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    // Ref to the scroll container so we can programmatically scroll to top when adding a row
    const listRef = useRef<HTMLDivElement | null>(null)

    /* ------------------------------ Infinite query fetch ---------------------------- */
    const fetchRows = async ({ pageParam = 1 }): Promise<{ next: string | null; results: APIRow[] }> => {
        const params = new URLSearchParams()
        params.append('limit', String(fetchLimit))
        params.append('page', String(pageParam))
        if (search) params.append('search', search)
        if (filterShopId !== '') params.append('shop_id', String(filterShopId))
        const res = await apiClient
            .get(`integrations/rainforest/${env}/locations/?${params.toString()}`)
            .json<{
                next: string | null
                results: [number, string, string][]
            }>()
        return {
            next: res.next,
            results: res.results.map(([shop_id, location, location_id]) => ({ shop_id, location, location_id })),
        }
    }

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery(
        ['rainforest-locations', env, search, filterShopId],
        fetchRows,
        {
            getNextPageParam: (lastPage) => {
                if (!lastPage.next) return undefined
                try {
                    const url = new URL(lastPage.next, window.location.origin)
                    const nextPageStr = url.searchParams.get('page')
                    return nextPageStr ? Number(nextPageStr) : undefined
                } catch {
                    return undefined
                }
            },
        },
    )

    const initialMapRef = useRef<Map<string, APIRow>>(new Map())

    const existingRows: APIRow[] = useMemo(() => {
        return data?.pages.flatMap((p) => p.results) ?? []
    }, [data])

    const netsuiteLocations: [string, string][] = useMemo(() => {
        const source = Array.isArray(locations) && locations.length ? locations : (settings.locations as unknown[]) || []
        const mapped: [string, string][] = (source as unknown[]).flatMap((item) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
                const obj = item as Record<string, unknown>
                const label = String(obj.name ?? obj.label ?? '')
                const value = String(obj.id ?? obj.value ?? '')
                return label ? [[label, value]] : []
            }
            return []
        })
        return mapped
    }, [locations, settings])

    const [rows, setRows] = useState<Row[]>([])
    const localKeyCounterRef = useRef(0)

    // When fetched rows change, initialise local state once
    useEffect(() => {
        setRows(existingRows.map((r, i) => ({ ...r, _key: `${r.shop_id}|${r.location}|${r.location_id}|${i}` })))
        initialMapRef.current = new Map(existingRows.map((r) => [`${r.shop_id}|${r.location}|${r.location_id}`, r]))
    }, [existingRows])

    // When changing the shop filter, clear state only when selecting All Connections from a specific shop
    const prevFilterShopIdRef = useRef<number | ''>(filterShopId)
    useEffect(() => {
        const prevFilterShopId = prevFilterShopIdRef.current
        prevFilterShopIdRef.current = filterShopId
        
        // Only clear state when switching FROM a specific shop TO All Connections
        // This preserves the "All Connections" data when it's already selected
        if (filterShopId === '' && prevFilterShopId !== '') {
            setRows([])
            if (listRef.current) {
                listRef.current.scrollTo({ top: 0 })
            }
            queryClient.removeQueries({ queryKey: ['rainforest-locations', env], exact: false })
        }
    }, [filterShopId, env, queryClient])

    const handleBulkOpen = () => {
        setBulkOpen(true)
        setUploadSuccess(false)
    }

    const handleBulkClose = () => {
        if (uploading) return
        setBulkOpen(false)
    }

    const parseAndUpload = async (file: File) => {
        setUploading(true)
        try {
            const text = await file.text()
            const parsed = Papa.parse<string[]>(text.trim(), { skipEmptyLines: true })
            if (parsed.errors.length) throw new Error('Failed to parse CSV')
            const csvRows: [number, string, string][] = (parsed.data as string[][]).map((r) => [Number(r[0]), r[1], r[2]])
            await apiClient.post(`integrations/rainforest/${env}/locations/`, { json: csvRows })
            setUploadSuccess(true)
            await queryClient.invalidateQueries(['rainforest-locations', env])
        } catch (err) {
            console.error(err)
            alert('Upload failed')
        } finally {
            setUploading(false)
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) void parseAndUpload(file)
    }

    const downloadCSV = async () => {
        setDownloading(true)
        try {
            // Fetch all records by making multiple API calls with timeout protection
            let allRows: APIRow[] = []
            let page = 1
            let hasNext = true
            const maxPages = 1000 // Safety limit: max 1000 pages (25,000 records)
            const timeoutMs = 30000 // 30 seconds timeout
            
            const startTime = Date.now()
            
            while (hasNext && page <= maxPages) {
                // Check timeout
                if (Date.now() - startTime > timeoutMs) {
                    throw new Error('Download timeout: operation took too long')
                }
                
                const params = new URLSearchParams()
                params.append('limit', String(fetchLimit))
                params.append('page', String(page))
                if (search) params.append('search', search)
                if (filterShopId !== '') params.append('shop_id', String(filterShopId))
                
                const res = await apiClient
                    .get(`integrations/rainforest/${env}/locations/?${params.toString()}`)
                    .json<{
                        next: string | null
                        results: [number, string, string][]
                    }>()
                
                allRows = [...allRows, ...res.results.map(([shop_id, location, location_id]) => ({ shop_id, location, location_id }))]
                hasNext = res.next !== null
                page++
            }
            
            if (page > maxPages) {
                throw new Error('Download limit exceeded: too many records')
            }
            
            // Convert to CSV format
            const csvData = allRows.map(row => [row.shop_id, row.location, row.location_id])
            const csv = Papa.unparse(csvData)
            
            // Create download link
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', `shopify_locations_${env}_${new Date().toISOString().split('T')[0]}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (err) {
            console.error('Failed to download CSV:', err)
            const errorMessage = err instanceof Error ? err.message : 'Download failed'
            alert(errorMessage)
        } finally {
            setDownloading(false)
        }
    }

    const handleAdd = () => {
        const nextKey = `local-${localKeyCounterRef.current++}`
        setRows((prev) => [{ shop_id: '', location: '', location_id: '', _key: nextKey, _isNew: true }, ...prev])
        // Scroll to the top so the newly added row is immediately visible
        if (listRef.current) {
            listRef.current.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleDelete = (idx: number) => {
        setRows((prev) =>
            prev.map((r, i) => (i === idx ? { ...r, _isDeleted: true, _isNew: false } : r)),
        )
    }

    // ----------------------------- Validation helper -----------------------------
    const str_convertor = (v: unknown) => (typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v))

    const rowIsValid = (r: Row) => r.shop_id !== '' && r.location.trim() && str_convertor(r.location_id).trim()

    const validateRows = (): boolean => {
        let allValid = true
        setRows((prev) =>
            prev.map((r) => {
                if (r._isDeleted) return r
                const errors = {
                    shop_id: r.shop_id === '',
                    location: r.location.trim() === '',
                    location_id: str_convertor(r.location_id).trim() === '',
                }
                if (errors.shop_id || errors.location || errors.location_id) {
                    allValid = false
                }
                return { ...r, _errors: errors }
            }),
        )
        return allValid
    }

    const handleChange = <K extends keyof Row>(idx: number, field: K, val: Row[K]) => {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== idx) return r
                const newRow = { ...r, [field]: val, _isNew: true }
                if (newRow._errors) {
                    newRow._errors[field as keyof typeof newRow._errors] = val === '' || (typeof val === 'string' && val.trim() === '')
                }
                return newRow
            }),
        )
    }

    const renderSelect = (
        idx: number,
        field: 'shop_id' | 'location_id',
        options: [string, string | number][],
        hasError: boolean,
    ) => {
        const optionObjs = options.map(([label, value]) => ({ label, value }))
        return (
            <AutocompleteSelect
                options={optionObjs}
                value={rows[idx][field] as string | number}
                onChange={(val) => handleChange(idx, field, val as Row[typeof field])}
                fullWidth
                error={hasError}
            />
        )
    }

    /* --------------------------- Infinite scroll handler ------------------------------ */
    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasNextPage || isFetchingNextPage) return
        const target = e.target as HTMLDivElement
        if (target.scrollHeight - target.scrollTop - target.clientHeight < 50) {
            fetchNextPage().catch(console.error)
        }
    }

    /* --------------------------------------- UI --------------------------------------- */
    useImperativeHandle(ref, () => ({
        validate: validateRows,
        getDirtyRows: () =>
            rows
                .filter(
                    (r) =>
                        (r._isNew && rowIsValid(r)) ||
                        (r._isDeleted && r.shop_id !== '' && r.location.trim()),
                )
                .map((r) => [
                    Number(r.shop_id),
                    r.location.trim(),
                    r._isDeleted ? null : str_convertor(r.location_id).trim(),
                ] as [number, string, string | null]),
        resetDirty: () =>
            setRows((prev) => prev.filter((r) => !r._isDeleted).map((r) => ({ ...r, _isNew: false }))),
    }), [rows]);

    // Preserve original indices for handlers while filtering out deleted rows
    const visibleRows = useMemo(() => rows.map((r, i) => ({ row: r, idx: i })).filter(({ row }) => !row._isDeleted), [rows])

    return (
        <Paper elevation={0} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ width: 160, flexShrink: 0 }}>
                    <AutocompleteSelect
                        options={[
                            { label: 'All Connections', value: '' },
                            ...(shops ?? [])
                                .filter((s) => s.id !== WALMART_SHOP_ID)
                                .map((s) => ({ label: s.shop_name, value: s.id })),
                        ]}
                        value={filterShopId}
                        onChange={(val) => setFilterShopId(val === '' ? '' : Number(val))}
                        fullWidth
                    />
                </Box>
                <TextField
                     label="Search Location"
                     variant="outlined"
                     size="small"
                     color="secondary"
                     margin="none"
                     InputLabelProps={{ shrink: true }}
                     value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     sx={{ width: 160, '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 }, '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' } }}
                 />
                <Button variant="outlined" color="secondary" size="small" onClick={() => { setSearch(''); setFilterShopId(''); }}>
                    Clear
                </Button>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button variant="outlined" color="secondary" size="small" startIcon={downloading ? <CircularProgress size={16} /> : <CloudDownloadIcon />} onClick={downloadCSV} disabled={!existingRows.length || downloading}>
                    {downloading ? 'Downloading...' : 'Download'}
                </Button>
                <Button variant="outlined" color="secondary" size="small" startIcon={<CloudUploadIcon />} onClick={handleBulkOpen}>
                    Bulk Upload
                </Button>
                <Button variant="outlined" color="secondary" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
                    Add
                </Button>
            </Stack>




                <Dialog open={bulkOpen} onClose={handleBulkClose} maxWidth="sm" fullWidth>
                    <DialogTitle>Bulk Upload</DialogTitle>
                    <DialogContent sx={{ padding: "24px" }}>
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, fontSize: '0.875rem' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>File Format Instructions:</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>• File must be CSV format with 3 columns (no header needed):</Typography>
                            <Typography variant="body2" component="div" sx={{ pl: 2, mb: 1 }}>
                              1. connection_id (Connection ID)<br/>
                              2. location (Location Name)<br/>
                              3. location_id (NetSuite Location ID)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>• Mapping behavior:</Typography>
                            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                              • Updates existing mappings or creates new ones<br/>
                              • To remove a mapping, leave location_id field empty
                            </Typography>
                        </Box>
                        
                        {uploading ? (
                            <Box sx={{ textAlign: 'center' }}>
                                <CircularProgress />
                                <Typography variant="body2" sx={{ mt: 1 }}>Uploading...</Typography>
                            </Box>
                        ) : uploadSuccess ? (
                            <Stack alignItems="center" spacing={1}>
                                <CheckCircleIcon color="primary" fontSize="large" />
                                <Box>Successful upload</Box>
                            </Stack>
                        ) : (
                            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={uploading} color="secondary" fullWidth>
                                Upload CSV
                                <input type="file" accept=".csv" hidden ref={fileInputRef} onChange={handleFileChange} />
                            </Button>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleBulkClose} disabled={uploading} color="secondary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Table */}
                {isLoading ? (
                    <Table size="small" stickyHeader sx={{ mt: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Connection</TableCell>
                                <TableCell>SKU</TableCell>
                                <TableCell>Netsuite Location</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    {Array.from({ length: 4 }).map((__, colIdx) => (
                                        <TableCell key={colIdx} sx={{ px: 1, py: 0.75 }}>
                                            <Skeleton variant="text" height={24} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : rows.length === 0 ? (
                    <Alert severity="info" variant="outlined" sx={{ mt: 1, color: 'text.secondary', bgcolor: 'grey.100', borderColor: 'grey.300', '& .MuiAlert-icon': { color: 'text.secondary' } }}>
                        No locations yet
                    </Alert>
                ) : (
                    <Box ref={listRef} sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }} onScroll={onScroll}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>Connections</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>SKU</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>Netsuite Location</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows.map(({ row, idx }, displayIdx) => (
                                    <TableRow key={row._key} sx={{ bgcolor: displayIdx % 2 ? 'grey.50' : 'common.white' }}>
                                        <TableCell sx={{ px: 1, py: 0.75 }}>
                                            {renderSelect(idx, 'shop_id', (shops ?? []).filter((s) => s.id !== WALMART_SHOP_ID).map((s) => [s.shop_name, s.id]), Boolean(row._errors?.shop_id))}
                                        </TableCell>
                                        <TableCell sx={{ px: 1, py: 0.75 }}>
                                            <TextField
                                                variant="outlined"
                                                color="secondary"
                                                size="small"
                                                margin="none"
                                                value={row.location}
                                                error={Boolean(row._errors?.location)}
                                                onChange={(e) => handleChange(idx, 'location', e.target.value)}
                                                fullWidth
                                                sx={{ '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 }, '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' } }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ px: 1, py: 0.75 }}>
                                            {renderSelect(idx, 'location_id', netsuiteLocations, Boolean(row._errors?.location_id))}
                                        </TableCell>
                                        <TableCell sx={{ width: 96, py: 0.75, px: 1 }}>
                                            <IconButton color="inherit" sx={{ color: 'grey.500' }} onClick={() => handleDelete(idx)} size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {isFetchingNextPage && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                            <CircularProgress size={20} />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                )}
        </Paper>
    )
})

export default ShopifyLocations
